import React, { useContext, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
// TODO: verify it is importing the correct schema
import { Budget } from '@bingads-webui/campaign-odata-schemas';
import { BudgetComponent } from '@bingads-webui-campaign-react/budget-component';
import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';
import { FormErrorGroup } from '@bingads-webui-campaign-react/form-error';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useFormService, FORM_STATES } from '@bingads-webui-react/form';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  Collapse,
  ValidatedPanel,
  useCollapseState,
  collapseExpander,
  TextTitle,
  TitleBarError,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import {
  BidStrategyComponent,
  MAX_CONVERSIONS,
  MAX_CONVERSIONS_VALUE
} from '@bingads-webui-campaign-react/bid-strategy-component';
import { usePilotsValue } from '@bingads-webui-campaign-react/basic-context-utils';
import { CampaignTypes } from '@bingads-webui-campaign-react/constants';
import { BudgetSuggestionContainer, MODE, CustomBudget } from '@bingads-webui-campaign-react/budget-suggestion-v2';
import { BudgetStepSection, AllBudgetStepSections } from '../utils/const';
import { useLocalizeBudget } from '../utils/use-budget-localized';
import { useBudgetSuggestionData } from '../utils/use-budget-suggestion-data';
import BidStrategyIcon from '../../media/bid-strategy.svg';
import BudgetIcon from '../../media/budget.svg';

import { getBudgetFormStyles } from './budget-form-styles';

const useBudgetCollapseState = () => {
  const [
    allSections,
    expandedSections,
    setExpandedSections,
  ] = useCollapseState(AllBudgetStepSections, AllBudgetStepSections);
  const expandSections = useMemo(() =>
    collapseExpander({ setActiveKeys: setExpandedSections }), [setExpandedSections]);

  return {
    allSections,
    expandedSections,
    setExpandedSections,
    expandSections,
  };
};

const BID_STRATEGY_OPTIONS = [
  MAX_CONVERSIONS,
  MAX_CONVERSIONS_VALUE,
];

const BID_STRATEGY_OPTIONS_FOR_SMARTPAGE = [
  MAX_CONVERSIONS,
];

export const BudgetForm = ({
  data,
  errors = [],
  onDataChange,
  logger,
  hideBidStrategy,
  hideCurrencyDropdown,
}) => {
  const i18n = useContext(I18nContext);

  const {
    allSections,
    expandedSections,
    setExpandedSections,
    expandSections,
  } = useBudgetCollapseState();

  const {
    currencies = [],
    budgetAmount,
    currency,
    budgetSuggestion = {},
    bidStrategy,
    cpa,
    roas,
    isBudgetValid,
    budgetErrors,
    marketingMetricErrors,
    isInitialAdditionalDetailsChecked,
    isSmartPage,
    campaignForSuggestion,
    assetGroupForSuggestion,
    budgetTier,
  } = data || {};

  const { state } = useFormService();
  const isLoading = state.matches(FORM_STATES.LOADING);
  const getStyles = useResponsiveStyles(getBudgetFormStyles);
  const { localizeBudget } = useLocalizeBudget();
  const isBudgetSuggestionEnabled = usePilotsValue('isPmaxLiteBudgetSuggestionEnabled');
  const [triggerBudgetSuggestion, setTriggerBudgetSuggestion] = useState(false);
  const minbudgetSuggestion = _.get(budgetSuggestion, ['GlobalMinimumBudget', 'DailyBudget']);
  const maxbudgetSuggestion = _.get(budgetSuggestion, ['GlobalMaximumBudget', 'DailyBudget']);

  const onBudgetSuggestionDone = useCallback(() => setTriggerBudgetSuggestion(false), [setTriggerBudgetSuggestion]);

  const {
    isBudgetSuggestionLoading,
    customPerformanceData,
    budgetSuggestionData,
    saveBudgetData,
  } = useBudgetSuggestionData({
    cpa,
    roas,
    bidStrategy,
    currency,
    campaign: campaignForSuggestion,
    assetGroup: assetGroupForSuggestion,
    budgetTier,
    budgetAmount,
    isBudgetValid,
    enableBudgetSuggestion: isBudgetSuggestionEnabled,
    triggerBudgetSuggestion,
    onBudgetSuggestionDone,
    logger,
  });

  const defaultSuggestBudget = useMemo(() => {
    if (budgetTier && budgetTier !== CustomBudget && !_.isEmpty(budgetSuggestionData)) {
      return _.get(budgetSuggestionData, [budgetTier, 'BudgetAmountDaily']);
    }

    return budgetAmount;
  }, [budgetAmount, budgetSuggestionData, budgetTier]);

  const onFieldChange = useCallback((updatedData) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        ...updatedData,
      }),
    });
  }, [onDataChange]);

  const onBidStrategyChange = useCallback((e, newBidStrategy) => {
    const bidStrategyType = _.get(newBidStrategy, 'type');
    onFieldChange({ bidStrategy: bidStrategyType });
  }, [onFieldChange]);

  const onBudgetChange = useCallback((newBudget, shouldLocalize = false) => {
    const budgetAmountValue = shouldLocalize ? localizeBudget(newBudget, currency) : newBudget;
    // when budget is changed, we need to reset the validation state to false first
    onFieldChange({ budgetAmount: budgetAmountValue, isBudgetValid: false });
  }, [currency, onFieldChange, localizeBudget]);

  const onCurrencyChange = useCallback((newCurrency) => {
    onFieldChange({ currency: newCurrency });
  }, [onFieldChange]);

  const onBudgetValidate = useCallback(({ isValid, messages }) => {
    onFieldChange({ isBudgetValid: isValid, budgetErrors: messages });
  }, [onFieldChange]);

  const onBudgetSuggestionValidate = useCallback((result) => {
    const { data: suggestionData = {}, isValid, messages } = result || {};

    logger.trace(`budget suggestion validate, data: ${JSON.stringify(suggestionData)}, isValid: ${isValid}, messages: ${JSON.stringify(messages)}`);

    onFieldChange({
      budgetTier: suggestionData.budgetTier,
      budgetAmount: suggestionData.budget ? `${suggestionData.budget}` : '',
      isBudgetValid: isValid,
      budgetErrors: messages,
      customSaveBudgetPromise: biddingScheme => saveBudgetData({
        budgetAmount: suggestionData.budget ? `${suggestionData.budget}` : '',
        budgetTier: suggestionData.budgetTier,
        biddingScheme,
      }),
    });
  }, [logger, onFieldChange, saveBudgetData]);

  const onSelectCurrency = useCallback((e, selectedData) => {
    setTriggerBudgetSuggestion(true);
    logger.trace(`budget suggestion currency change: ${selectedData.optionValue}`);
    onCurrencyChange(selectedData.optionValue || '');
  }, [logger, onCurrencyChange]);

  const serializeCurrencies = useMemo(() => {
    if (_.isEmpty(currencies)) {
      return [];
    }

    return currencies.map(c => ({
      key: _.get(c, ['value', 'Code']),
      code: _.get(c, ['value', 'Code']),
      text: _.get(c, ['value', 'DisplayName']),
    }));
  }, [currencies]);

  const onMarketingMetricChange = useCallback(({
    data: newMarketingMetric,
    isValid,
    messages,
  }) => {
    onFieldChange({
      ...newMarketingMetric,
      isMarketingMetricValid: isValid,
      marketingMetricErrors: messages,
    });
  }, [onFieldChange]);

  const bidStrategyMessageContent = i18n.getString(_TL_('You must set up conversion tracking after creating your campaign for your bid strategy to take effect. Please note that if you don\'t set up conversion tracking, your bid strategy will automatically update to maximize clicks on your website instead of optimizing for your conversion goals. This is less optimal for campaign performance as your website may not receive as much traffic generating value for your business.'));

  const isBudgetCollapsed = !_.contains(expandedSections, BudgetStepSection.Budget);
  const isBidStrategyCollapsed = !_.contains(expandedSections, BudgetStepSection.BidStrategy);

  const serverContext = useContext(ServerContext);
  const bidRange = (serverContext && serverContext.KeywordBidRange) || null;

  return (
    <React.Fragment>
      <div className={classNames(getStyles('formContainer'))}>
        <FormErrorGroup formErrorProps={{ errors }}>
          <ValidationGroup
            validationProps={{
              i18n,
              schema: Budget,
              data,
              onValidate: () => { },
            }}
            error={isBudgetCollapsed && <TitleBarError error={_.isArray(budgetErrors) && budgetErrors[0]} />}
          >
            <Collapse
              checkItems
              skipItemsTypeCheck
              allPanelItems={allSections}
              activeKeys={expandedSections}
              setActiveKeys={setExpandedSections}
            >
              <ValidatedPanel
                key={BudgetStepSection.Budget}
                title={
                  <TextTitle
                    icon={BudgetIcon}
                    titleKey={_TL_('Budget')}
                    topicKey="pop_BA_PMAXLite_Budget"
                    as="h2"
                    headingClassName={classNames(getStyles('budgetTitle'))}
                  />
                }
                actuationProps={{
                  expandKey: BudgetStepSection.Budget,
                  expand: expandSections,
                }}
                error={isBudgetCollapsed && <TitleBarError error={_.isArray(budgetErrors) && budgetErrors[0]} />}
              >
                {
                  isBudgetSuggestionEnabled ? (
                    <BudgetSuggestionContainer
                      isLoading={isLoading || isBudgetSuggestionLoading || triggerBudgetSuggestion}
                      data={budgetSuggestionData}
                      mode={MODE.CREATION}
                      currency={currency}
                      campaignType={CampaignTypes.PerformanceMax}
                      onBudgetValidate={onBudgetSuggestionValidate}
                      customPerformanceData={customPerformanceData}
                      defaultBudget={defaultSuggestBudget}
                      bidType={bidStrategy}
                      minBudget={minbudgetSuggestion}
                      maxBudget={maxbudgetSuggestion}
                      onSelectCurrency={onSelectCurrency}
                      currencies={serializeCurrencies}
                      showDropdown
                    />
                  ) : (
                    <BudgetComponent
                      showDropdown={!hideCurrencyDropdown}
                      currencies={currencies}
                      currency={currency}
                      onCurrencyChange={onCurrencyChange}
                      budget={budgetAmount}
                      onBudgetChange={onBudgetChange}
                      budgetSuggestion={budgetSuggestion}
                      isLoading={isLoading}
                      onNotifyValidation={onBudgetValidate}
                      logger={logger}
                    />
                  )
                }
              </ValidatedPanel>
              {!hideBidStrategy && (
                <ValidatedPanel
                  key={BudgetStepSection.BidStrategy}
                  title={
                    <TextTitle
                      icon={BidStrategyIcon}
                      titleKey={_TL_('Bid strategy')}
                      topicKey="pop_BA_PMAX_BidStrategy"
                      as="h2"
                      headingClassName={classNames(getStyles('budgetTitle'))}
                    />
                  }
                  actuationProps={{
                    expandKey: BudgetStepSection.BidStrategy,
                    expand: expandSections,
                  }}
                  error={
                    isBidStrategyCollapsed
                    && <TitleBarError error={_.isArray(marketingMetricErrors) && marketingMetricErrors[0]} />
                  }
                >
                  <BidStrategyComponent
                    options={isSmartPage ? BID_STRATEGY_OPTIONS_FOR_SMARTPAGE : BID_STRATEGY_OPTIONS}
                    selectedBidStrategy={bidStrategy}
                    onBidStrategyChange={onBidStrategyChange}
                    defaultChecked={isInitialAdditionalDetailsChecked}
                    marketingMetricsValue={{ cpa, roas }}
                    onMarketingMetricChange={onMarketingMetricChange}
                    inputUnit={currency}
                    bidRange={bidRange}
                    messageBarType="info"
                    messageContent={bidStrategyMessageContent}
                    isLoading={isLoading}
                    budgetAmount={budgetAmount}
                    scenario="pmax-budget-form"
                    withTargetCpaWarning
                  />
                </ValidatedPanel>
              )}
            </Collapse>
          </ValidationGroup>
        </FormErrorGroup>
      </div >
    </React.Fragment >
  );
};

BudgetForm.propTypes = {
  data: PropTypes.shape({
    currencies: PropTypes.arrayOf(PropTypes.shape({})),
    currency: PropTypes.string,
    budgetAmount: PropTypes.number,
    selectedStrategy: PropTypes.string,
  }).isRequired,
  errors: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onDataChange: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
    error: PropTypes.func.isRequired,
  }),
  hideBidStrategy: PropTypes.bool.isRequired,
  hideCurrencyDropdown: PropTypes.bool.isRequired,
};

BudgetForm.defaultProps = {
  logger: {
    trace: _.noop,
    error: _.noop,
  },
};
